/**
 * Selección del backend de datos (perfil de despliegue).
 *
 * `VITE_BACKEND` se fija en build (por perfil) y Vite lo inlinea como literal,
 * así que el `if` de abajo permite al bundler ELIMINAR los backends no usados
 * (tree-shaking). Resultado: cada artefacto contiene SOLO su backend —
 * requisito de las tres versiones aisladas para auditoría.
 *
 *   VITE_BACKEND=supabase    → Cloudflare/Supabase (hoy) y Azure/PostgREST
 *   VITE_BACKEND=sharepoint  → SharePoint vía Microsoft Graph
 *   VITE_BACKEND=dexie       → local (desarrollo / demo offline)
 *
 * La construcción concreta de cada RepoSet vive en `repos.<backend>.ts` para
 * que el límite de cada backend sea explícito y fácil de auditar.
 */

import type { AfeRepo } from "./repos/AfeRepo";
import type { SoRepo } from "./repos/SoRepo";
import type { UsuarioRepo } from "./repos/UsuarioRepo";
import type { EventoRepo } from "./repos/EventoRepo";
import type { RdRepo } from "./repos/RdRepo";
import type { ActaRepo } from "./repos/ActaRepo";
import type { ContratoMarcoRepo } from "./repos/ContratoMarcoRepo";
import type { PrRepo } from "./repos/PrRepo";
import type { PoRepo } from "./repos/PoRepo";
import type { BudgetRepo } from "./repos/BudgetRepo";
import type { CosteCatalogoRepo } from "./repos/CosteCatalogoRepo";
import type { NotificacionConfigRepo } from "./repos/NotificacionConfigRepo";
import type { ListaDistribucionRepo } from "./repos/ListaDistribucionRepo";
import type { NotificacionEnviadaRepo } from "./repos/NotificacionEnviadaRepo";
import type { ConfiguracionIARepo } from "./repos/ConfiguracionIARepo";
import type { RiesgoRepo } from "./repos/RiesgoRepo";
import type { MejoraRepo } from "./repos/MejoraRepo";
import type { MrRepo } from "./repos/MrRepo";
import type { GastoSapRepo } from "./repos/GastoSapRepo";
import type { RegistroRecursosRepo } from "./repos/RegistroRecursosRepo";
import type { AuditLogRepo } from "./repos/AuditLogRepo";
import type { StakeholderRepo } from "./repos/StakeholderRepo";
import type { CatalogoRiesgosRepo } from "./repos/CatalogoRiesgosRepo";
import type { ComisionamientoRepo } from "./repos/ComisionamientoRepo";

import { buildDexieRepos } from "./repos.dexie";
import { buildSupabaseRepos } from "./repos.supabase";
import { buildSharepointRepos } from "./repos.sharepoint";

export interface RepoSet {
  afe: AfeRepo;
  so: SoRepo;
  usuario: UsuarioRepo;
  evento: EventoRepo;
  rd: RdRepo;
  acta: ActaRepo;
  contrato: ContratoMarcoRepo;
  pr: PrRepo;
  po: PoRepo;
  budget: BudgetRepo;
  costeCatalogo: CosteCatalogoRepo;
  notificacionConfig: NotificacionConfigRepo;
  listaDistribucion: ListaDistribucionRepo;
  notificacionEnviada: NotificacionEnviadaRepo;
  configuracionIA: ConfiguracionIARepo;
  riesgo: RiesgoRepo;
  stakeholder: StakeholderRepo;
  catalogoRiesgos: CatalogoRiesgosRepo;
  comisionamiento: ComisionamientoRepo;
  mejora: MejoraRepo;
  mr: MrRepo;
  gastoSap: GastoSapRepo;
  registroRecursos: RegistroRecursosRepo;
  auditLog: AuditLogRepo;
}

/** Backend del perfil de build (inlineado por Vite → habilita tree-shaking). */
const BACKEND = import.meta.env.VITE_BACKEND ?? "dexie";

export function buildRepos(tenantId: string): RepoSet {
  if (BACKEND === "sharepoint") return buildSharepointRepos(tenantId);
  if (BACKEND === "supabase") return buildSupabaseRepos(tenantId);
  if (BACKEND === "dexie") return buildDexieRepos(tenantId);
  throw new Error(
    `Backend "${BACKEND}" no disponible. Usa VITE_BACKEND=dexie, sharepoint o supabase.`,
  );
}
